import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { PermissionGate } from '../components/PermissionGate';
import { SearchBar } from '../components/SearchBar';
import { MobileBlogPreview } from '../features/helpArticles/components/MobileBlogPreview';
import { AuthenticatedImage } from '../shared/components/AuthenticatedImage';
import { AuthenticatedPdf } from '../shared/components/AuthenticatedPdf';
import {
  Badge,
  Button,
  Card,
  Input,
  SectionHeader,
  Select,
  Textarea,
  useActionDialog,
} from '../components/ui';
import {
  useClearHelpArticleHeroMedia,
  useCreateHelpArticle,
  useDeleteHelpArticle,
  useDeleteHelpArticlePreviewImage,
  useHelpArticle,
  useHelpArticles,
  usePublishHelpArticle,
  useSetHelpArticleYouTube,
  useUnpublishHelpArticle,
  useUpdateHelpArticle,
  useUploadHelpArticleHeroImage,
  useUploadHelpArticleHeroPdf,
  useUploadHelpArticleHeroVideo,
  useUploadHelpArticlePreviewImage,
} from '../features/helpArticles/hooks/useHelpArticles';
import { useAuth } from '../features/auth/AuthProvider';
import { ApiError } from '../api/client';
import type {
  HelpArticleDetail,
  HelpArticlePreviewFit,
  HelpArticleSection,
  HelpArticleUpsertPayload,
} from '../types/helpArticles';
import { HELP_ARTICLE_PDF_MAX_BYTES, validateHelpArticlePdf } from '../utils/fileValidation';
import styles from './HelpArticlesPage.module.scss';

type EditorState = {
  title: string;
  summary: string;
  sortOrder: string;
  sections: HelpArticleSection[];
  previewFit: HelpArticlePreviewFit;
  previewPositionX: number;
  previewPositionY: number;
};

type MediaTab = 'image' | 'video' | 'youtube' | 'pdf';

type MobilePreviewTarget =
  | { readonly kind: 'list' }
  | { readonly kind: 'editor' }
  | { readonly kind: 'article'; readonly articleId: string };

const emptyEditorState: EditorState = {
  title: '',
  summary: '',
  sortOrder: '0',
  sections: [{ heading: '', body: '' }],
  previewFit: 'cover',
  previewPositionX: 50,
  previewPositionY: 50,
};

const heroImageAccept =
  '.jpg,.jpeg,.png,.heic,.heif,.webp,image/jpeg,image/png,image/heic,image/heif,image/webp';
const previewImageAccept = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
const heroPdfMaxMb = HELP_ARTICLE_PDF_MAX_BYTES / (1024 * 1024);

function mediaTabFromHeroKind(
  heroKind: 'image' | 'video_upload' | 'video_youtube' | 'pdf' | null | undefined,
): MediaTab {
  if (heroKind === 'video_upload') return 'video';
  if (heroKind === 'video_youtube') return 'youtube';
  if (heroKind === 'pdf') return 'pdf';
  return 'image';
}

export function HelpArticlesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { confirmAction } = useActionDialog();
  const [search, setSearch] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);
  const [isCreating, setIsCreating] = useState(false);
  const [pendingHeroFile, setPendingHeroFile] = useState<File | null>(null);
  const [pendingVideoFile, setPendingVideoFile] = useState<File | null>(null);
  const [pendingPdfFile, setPendingPdfFile] = useState<File | null>(null);
  const [pendingPreviewFile, setPendingPreviewFile] = useState<File | null>(null);
  const [pdfFileError, setPdfFileError] = useState<string | null>(null);
  const [mediaTab, setMediaTab] = useState<MediaTab>('image');
  const [youtubeInput, setYoutubeInput] = useState<string>('');
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [mobilePreviewTarget, setMobilePreviewTarget] = useState<MobilePreviewTarget>({ kind: 'list' });
  const editorHydratedForArticleIdRef = useRef<string | null>(null);
  const mediaHydratedForArticleIdRef = useRef<string | null>(null);

  const { data: articles, isLoading, isError, error } = useHelpArticles();
  const { data: articleDetail } = useHelpArticle(selectedArticleId);
  const previewArticleId = mobilePreviewTarget.kind === 'article' ? mobilePreviewTarget.articleId : null;
  const { data: fetchedPreviewArticle, isLoading: isPreviewArticleLoading } = useHelpArticle(previewArticleId);
  const createMutation = useCreateHelpArticle();
  const updateMutation = useUpdateHelpArticle();
  const deleteMutation = useDeleteHelpArticle();
  const publishMutation = usePublishHelpArticle();
  const unpublishMutation = useUnpublishHelpArticle();
  const uploadHeroMutation = useUploadHelpArticleHeroImage();
  const uploadHeroVideoMutation = useUploadHelpArticleHeroVideo();
  const uploadHeroPdfMutation = useUploadHelpArticleHeroPdf();
  const uploadPreviewMutation = useUploadHelpArticlePreviewImage();
  const deletePreviewMutation = useDeleteHelpArticlePreviewImage();
  const setYoutubeMutation = useSetHelpArticleYouTube();
  const clearMediaMutation = useClearHelpArticleHeroMedia();
  const canDeleteArticles = user?.roles.includes('адмін') ?? false;

  useEffect(() => {
    if (isCreating) {
      editorHydratedForArticleIdRef.current = null;
      return;
    }
    if (!selectedArticleId || !articleDetail || articleDetail.id !== selectedArticleId) return;
    if (editorHydratedForArticleIdRef.current === selectedArticleId) return;

    editorHydratedForArticleIdRef.current = selectedArticleId;
    setEditor({
      title: articleDetail.title,
      summary: articleDetail.summary,
      sortOrder: String(articleDetail.sort_order),
      sections: articleDetail.sections.length > 0 ? [...articleDetail.sections] : [{ heading: '', body: '' }],
      previewFit: articleDetail.preview_fit ?? 'cover',
      previewPositionX: articleDetail.preview_position_x ?? 50,
      previewPositionY: articleDetail.preview_position_y ?? 50,
    });
  }, [articleDetail, isCreating, selectedArticleId]);

  useEffect(() => {
    if (isCreating) {
      mediaHydratedForArticleIdRef.current = null;
      return;
    }
    if (!selectedArticleId || !articleDetail || articleDetail.id !== selectedArticleId) return;
    if (mediaHydratedForArticleIdRef.current === selectedArticleId) return;

    mediaHydratedForArticleIdRef.current = selectedArticleId;
    setMediaTab(mediaTabFromHeroKind(articleDetail.hero_kind));
    setYoutubeInput(articleDetail.hero_youtube_url ?? '');
    setYoutubeError(null);
  }, [articleDetail, isCreating, selectedArticleId]);

  const filteredArticles = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) return articles ?? [];
    return (articles ?? []).filter((article) =>
      [article.title, article.summary, article.slug].some((value) => value.toLowerCase().includes(normalized)),
    );
  }, [articles, search]);

  const publishedArticles = useMemo(
    () => (articles ?? []).filter((article) => article.status === 'published'),
    [articles],
  );

  const editorPreviewArticle = useMemo<HelpArticleDetail | null>(() => {
    if (!selectedArticleId && !isCreating) return null;

    const draftHeroKind = pendingHeroFile
      ? 'image'
      : pendingVideoFile
        ? 'video_upload'
        : pendingPdfFile
          ? 'pdf'
          : null;

    return {
      id: selectedArticleId ?? 'unsaved-draft-preview',
      slug: articleDetail?.slug ?? '',
      title: editor.title || t('helpArticles.untitledPreview'),
      summary: editor.summary,
      status: articleDetail?.status ?? 'draft',
      sort_order: Number.parseInt(editor.sortOrder, 10) || 0,
      published_at: articleDetail?.published_at ?? null,
      hero_image_url: articleDetail?.hero_image_url ?? null,
      hero_kind: articleDetail?.hero_kind ?? draftHeroKind,
      hero_video_url: articleDetail?.hero_video_url ?? null,
      hero_youtube_url: articleDetail?.hero_youtube_url ?? null,
      hero_pdf_url: articleDetail?.hero_pdf_url ?? null,
      preview_image_url: articleDetail?.preview_image_url ?? null,
      preview_fit: editor.previewFit,
      preview_position_x: editor.previewPositionX,
      preview_position_y: editor.previewPositionY,
      updated_at: articleDetail?.updated_at ?? new Date(0).toISOString(),
      sections: editor.sections.filter((section) => section.heading.trim() && section.body.trim()),
    };
  }, [
    articleDetail,
    editor,
    isCreating,
    pendingHeroFile,
    pendingPdfFile,
    pendingVideoFile,
    selectedArticleId,
    t,
  ]);

  const mobileListArticles = useMemo(
    () => publishedArticles.map((article) => {
      if (
        isCreating ||
        article.id !== selectedArticleId ||
        articleDetail?.id !== selectedArticleId
      ) {
        return article;
      }
      return {
        ...article,
        title: editor.title || article.title,
        summary: editor.summary,
        preview_fit: editor.previewFit,
        preview_position_x: editor.previewPositionX,
        preview_position_y: editor.previewPositionY,
        preview_image_url: articleDetail.preview_image_url,
        hero_image_url: articleDetail.hero_image_url,
        hero_kind: articleDetail.hero_kind,
      };
    }),
    [articleDetail, editor, isCreating, publishedArticles, selectedArticleId],
  );

  const mobilePreviewArticle =
    mobilePreviewTarget.kind === 'editor' ? editorPreviewArticle : fetchedPreviewArticle ?? null;

  function openMobileListPreview() {
    setMobilePreviewTarget({ kind: 'list' });
    setMobilePreviewOpen(true);
  }

  function openEditorMobilePreview() {
    setMobilePreviewTarget({ kind: 'editor' });
    setMobilePreviewOpen(true);
  }

  function resetPdfUploadFeedback() {
    setPdfFileError(null);
    uploadHeroPdfMutation.reset();
  }

  function startCreating() {
    resetPdfUploadFeedback();
    editorHydratedForArticleIdRef.current = null;
    mediaHydratedForArticleIdRef.current = null;
    setSelectedArticleId(null);
    setIsCreating(true);
    setEditor(emptyEditorState);
    setPendingHeroFile(null);
    setPendingVideoFile(null);
    setPendingPdfFile(null);
    setPendingPreviewFile(null);
    setMediaTab('image');
    setYoutubeInput('');
    setYoutubeError(null);
  }

  function selectArticle(articleId: string) {
    if (articleId !== selectedArticleId) {
      editorHydratedForArticleIdRef.current = null;
      mediaHydratedForArticleIdRef.current = null;
      resetPdfUploadFeedback();
    }
    setSelectedArticleId(articleId);
    setIsCreating(false);
    setPendingHeroFile(null);
    setPendingVideoFile(null);
    setPendingPdfFile(null);
    setPendingPreviewFile(null);
    setYoutubeError(null);
  }

  function updateSection(index: number, next: Partial<HelpArticleSection>) {
    setEditor((current) => ({
      ...current,
      sections: current.sections.map((section, sectionIndex) =>
        sectionIndex === index ? { ...section, ...next } : section,
      ),
    }));
  }

  function addSection() {
    setEditor((current) => ({
      ...current,
      sections: [...current.sections, { heading: '', body: '' }],
    }));
  }

  function removeSection(index: number) {
    setEditor((current) => ({
      ...current,
      sections: current.sections.filter((_, sectionIndex) => sectionIndex !== index),
    }));
  }

  async function handleSave() {
    const payload: HelpArticleUpsertPayload = {
      title: editor.title.trim(),
      summary: editor.summary.trim(),
      sort_order: Number.parseInt(editor.sortOrder, 10) || 0,
      sections: editor.sections
        .map((section) => ({
          heading: section.heading.trim(),
          body: section.body.trim(),
        }))
        .filter((section) => section.heading && section.body),
      preview_fit: editor.previewFit,
      preview_position_x: editor.previewPositionX,
      preview_position_y: editor.previewPositionY,
    };

    if (selectedArticleId && !isCreating) {
      await updateMutation.mutateAsync({ articleId: selectedArticleId, payload });
      return;
    }

    const created = await createMutation.mutateAsync(payload);
    setSelectedArticleId(created.id);
    setIsCreating(false);
    const heroFile = pendingHeroFile;
    const videoFile = pendingVideoFile;
    const pdfFile = pendingPdfFile;
    const previewFile = pendingPreviewFile;
    setPendingHeroFile(null);
    setPendingVideoFile(null);
    setPendingPdfFile(null);
    setPendingPreviewFile(null);

    const uploads: Promise<unknown>[] = [];
    if (mediaTab === 'image' && heroFile) {
      uploads.push(uploadHeroMutation.mutateAsync({ articleId: created.id, file: heroFile }));
    } else if (mediaTab === 'video' && videoFile) {
      uploads.push(uploadHeroVideoMutation.mutateAsync({ articleId: created.id, file: videoFile }));
    } else if (mediaTab === 'pdf' && pdfFile) {
      uploads.push(uploadHeroPdfMutation.mutateAsync({ articleId: created.id, file: pdfFile }));
    }
    if (previewFile) {
      uploads.push(uploadPreviewMutation.mutateAsync({ articleId: created.id, file: previewFile }));
    }
    await Promise.all(uploads);
  }

  async function handlePublishToggle() {
    if (!selectedArticleId || !articleDetail) return;
    if (articleDetail.status === 'published') {
      await unpublishMutation.mutateAsync(selectedArticleId);
      return;
    }
    await publishMutation.mutateAsync(selectedArticleId);
  }

  async function handleDeleteArticle() {
    if (!selectedArticleId || !articleDetail || !canDeleteArticles) return;
    const confirmed = await confirmAction({
      message: t('helpArticles.deleteArticleConfirm', { title: articleDetail.title }),
      confirmLabel: t('helpArticles.deleteArticle'),
      tone: 'danger',
    });
    if (!confirmed) return;

    await deleteMutation.mutateAsync(selectedArticleId);
    editorHydratedForArticleIdRef.current = null;
    mediaHydratedForArticleIdRef.current = null;
    setSelectedArticleId(null);
    setEditor(emptyEditorState);
    setPendingHeroFile(null);
    setPendingVideoFile(null);
    setPendingPdfFile(null);
    setPendingPreviewFile(null);
    setYoutubeInput('');
    setYoutubeError(null);
  }

  async function handleHeroImageChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (isCreating) {
      setPendingHeroFile(file);
      return;
    }
    if (!selectedArticleId) return;
    await uploadHeroMutation.mutateAsync({ articleId: selectedArticleId, file });
  }

  async function handleHeroVideoChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (isCreating) {
      setPendingVideoFile(file);
      return;
    }
    if (!selectedArticleId) return;
    await uploadHeroVideoMutation.mutateAsync({ articleId: selectedArticleId, file });
  }

  async function handleHeroPdfChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (validateHelpArticlePdf(file) === 'size') {
      setPdfFileError(t('helpArticles.pdfTooLarge', { maxMb: heroPdfMaxMb }));
      return;
    }
    setPdfFileError(null);
    if (isCreating) {
      setPendingPdfFile(file);
      return;
    }
    if (!selectedArticleId) return;
    await uploadHeroPdfMutation.mutateAsync({ articleId: selectedArticleId, file });
  }

  async function handlePreviewImageChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (isCreating) {
      setPendingPreviewFile(file);
      return;
    }
    if (!selectedArticleId) return;
    await uploadPreviewMutation.mutateAsync({ articleId: selectedArticleId, file });
  }

  async function handleRemovePreviewImage() {
    if (isCreating) {
      setPendingPreviewFile(null);
      return;
    }
    if (!selectedArticleId || !articleDetail?.preview_image_url) return;
    await deletePreviewMutation.mutateAsync(selectedArticleId);
  }

  async function handleSaveYoutube() {
    if (!selectedArticleId || isCreating) return;
    const trimmed = youtubeInput.trim();
    if (!trimmed) return;
    setYoutubeError(null);
    try {
      await setYoutubeMutation.mutateAsync({ articleId: selectedArticleId, youtubeUrl: trimmed });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 422) {
        setYoutubeError(t('helpArticles.invalidYoutubeUrl'));
        return;
      }
      throw err;
    }
  }

  async function handleRemoveMedia() {
    if (!selectedArticleId || isCreating) return;
    await clearMediaMutation.mutateAsync(selectedArticleId);
    setYoutubeInput('');
    setYoutubeError(null);
  }

  // Surface the backend error from any save-path mutation (e.g. a 422 on create)
  // so the admin sees why the form "did nothing" instead of a silent no-op.
  const saveError =
    createMutation.error ??
    updateMutation.error ??
    uploadHeroMutation.error ??
    uploadHeroVideoMutation.error ??
    deletePreviewMutation.error ??
    deleteMutation.error;
  const saveErrorMessage = saveError instanceof Error ? saveError.message : null;
  const previewUploadErrorMessage =
    uploadPreviewMutation.error instanceof Error ? uploadPreviewMutation.error.message : null;
  // Shown inside the PDF row: the save-button error slot is off-screen while picking a file.
  const pdfUploadErrorMessage =
    pdfFileError ??
    (uploadHeroPdfMutation.error instanceof Error ? uploadHeroPdfMutation.error.message : null);
  const dedicatedPreviewPath = isCreating ? null : articleDetail?.preview_image_url ?? null;
  const heroFallbackPath = isCreating ? null : articleDetail?.hero_image_url ?? null;
  const pendingHeroFallbackFile = isCreating && mediaTab === 'image' ? pendingHeroFile : null;
  const previewPath = dedicatedPreviewPath ?? heroFallbackPath;
  const previewFile = pendingPreviewFile ?? pendingHeroFallbackFile;
  const hasDedicatedPreview = Boolean(pendingPreviewFile || dedicatedPreviewPath);
  const hasHeroFallback = Boolean(pendingHeroFallbackFile || heroFallbackPath);

  return (
    <PermissionGate
      permission="help_articles:manage"
      fallback={
        <AppShell>
          <EmptyState title={t('helpArticles.forbidden')} description={t('helpArticles.forbiddenDescription')} />
        </AppShell>
      }
    >
      <AppShell>
        <div className={styles.page}>
          <SectionHeader
            title={t('helpArticles.title')}
            subtitle={t('helpArticles.subtitleCount', { count: articles?.length ?? 0 })}
            actions={
              <div className={styles.pageHeaderActions}>
                <Button variant="secondary" onClick={openMobileListPreview}>
                  {t('helpArticles.mobileAppPreview')}
                </Button>
                <Button variant="primary" onClick={startCreating}>
                  {t('helpArticles.newArticle')}
                </Button>
              </div>
            }
          />

          <div className={styles.layout}>
            <Card padding="md" className={styles.panel}>
              <SearchBar value={search} onChange={setSearch} placeholder={t('helpArticles.searchPlaceholder')} />
              <div className={styles.filterRow}>
                <span className={styles.filterLabel}>{t('helpArticles.library')}</span>
                <span className={styles.filterMeta}>
                  {t('helpArticles.itemsCount', { count: filteredArticles.length })}
                </span>
              </div>

              {isLoading ? (
                <div className={styles.placeholder}>{t('helpArticles.loadingArticles')}</div>
              ) : isError ? (
                <EmptyState
                  title={t('helpArticles.unableToLoad')}
                  description={error instanceof Error ? error.message : t('helpArticles.tryAgain')}
                />
              ) : filteredArticles.length === 0 ? (
                <EmptyState
                  title={t('helpArticles.noArticlesFound')}
                  description={t('helpArticles.noArticlesDescription')}
                />
              ) : (
                <div className={styles.articleList}>
                  {filteredArticles.map((article) => {
                    const active = selectedArticleId === article.id && !isCreating;
                    return (
                      <button
                        type="button"
                        key={article.id}
                        className={`${styles.articleCard} ${active ? styles.articleCardActive : ''}`}
                        onClick={() => selectArticle(article.id)}
                      >
                        <div className={styles.articleCardHeader}>
                          <h3>{article.title}</h3>
                          <Badge
                            variant={article.status === 'published' ? 'success' : 'warning'}
                            size="sm"
                          >
                            {article.status === 'published'
                              ? t('helpArticles.statusPublished')
                              : t('helpArticles.statusDraft')}
                          </Badge>
                        </div>
                        <p className={styles.articleCardSummary}>{article.summary}</p>
                        <div className={styles.articleMeta}>
                          <span>/{article.slug}</span>
                          <span>{t('helpArticles.orderLabel', { order: article.sort_order })}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card padding="md" className={styles.editorPanel}>
              <div className={styles.editorHeader}>
                <div>
                  <h3>{isCreating ? t('helpArticles.createArticle') : articleDetail?.title ?? t('helpArticles.selectArticle')}</h3>
                  <p>{t('helpArticles.draftHint')}</p>
                </div>
                {selectedArticleId || isCreating ? (
                  <div className={styles.editorHeaderActions}>
                    <Button variant="secondary" onClick={openEditorMobilePreview}>
                      {t('helpArticles.previewArticle')}
                    </Button>
                    {selectedArticleId && !isCreating ? (
                      <>
                        <Button variant="secondary" onClick={() => void handlePublishToggle()}>
                          {articleDetail?.status === 'published'
                            ? t('helpArticles.moveToDraft')
                            : t('helpArticles.publish')}
                        </Button>
                        {canDeleteArticles ? (
                          <Button
                            variant="danger"
                            onClick={() => void handleDeleteArticle()}
                            disabled={deleteMutation.isPending}
                          >
                            {t('helpArticles.deleteArticle')}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {!selectedArticleId && !isCreating ? (
                <EmptyState
                  title={t('helpArticles.noArticleSelected')}
                  description={t('helpArticles.noArticleSelectedDescription')}
                />
              ) : (
                <div className={styles.form}>
                  <Input
                    label={t('helpArticles.titleLabel')}
                    value={editor.title}
                    onChange={(event) => setEditor((current) => ({ ...current, title: event.target.value }))}
                  />
                  <Textarea
                    label={t('helpArticles.summaryLabel')}
                    value={editor.summary}
                    rows={3}
                    onChange={(event) => setEditor((current) => ({ ...current, summary: event.target.value }))}
                  />
                  <Input
                    label={t('helpArticles.sortOrderLabel')}
                    type="number"
                    value={editor.sortOrder}
                    onChange={(event) => setEditor((current) => ({ ...current, sortOrder: event.target.value }))}
                  />

                  <div className={styles.mediaTabs}>
                    <button
                      type="button"
                      className={`${styles.mediaTab} ${mediaTab === 'image' ? styles.mediaTabActive : ''}`}
                      onClick={() => setMediaTab('image')}
                    >
                      {t('helpArticles.mediaTabImage')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.mediaTab} ${mediaTab === 'video' ? styles.mediaTabActive : ''}`}
                      onClick={() => setMediaTab('video')}
                    >
                      {t('helpArticles.mediaTabVideo')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.mediaTab} ${mediaTab === 'youtube' ? styles.mediaTabActive : ''}`}
                      onClick={() => setMediaTab('youtube')}
                    >
                      {t('helpArticles.mediaTabYoutube')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.mediaTab} ${mediaTab === 'pdf' ? styles.mediaTabActive : ''}`}
                      onClick={() => setMediaTab('pdf')}
                    >
                      {t('helpArticles.mediaTabPdf')}
                    </button>
                    <span className={styles.mediaTabsSpacer} />
                    {!isCreating && articleDetail?.hero_kind ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => void handleRemoveMedia()}
                        disabled={clearMediaMutation.isPending}
                      >
                        {t('helpArticles.removeMedia')}
                      </Button>
                    ) : null}
                  </div>

                  {mediaTab === 'image' ? (
                    <div className={styles.uploadRow}>
                      <div>
                        <div className={styles.uploadTitle}>{t('helpArticles.heroImage')}</div>
                        <div className={styles.uploadMeta}>
                          {isCreating && pendingHeroFile ? (
                            <div className={styles.pendingHero}>
                              <div className={styles.pendingHeroName} title={pendingHeroFile.name}>
                                {pendingHeroFile.name}
                              </div>
                              <p className={styles.pendingHeroHint}>{t('helpArticles.heroImagePendingHint')}</p>
                              <button
                                type="button"
                                className={styles.clearPendingHero}
                                onClick={() => setPendingHeroFile(null)}
                              >
                                {t('helpArticles.clearPendingHero')}
                              </button>
                            </div>
                          ) : articleDetail?.hero_image_url && articleDetail?.hero_kind === 'image' ? (
                            t('helpArticles.imageUploaded')
                          ) : (
                            t('helpArticles.noImageUploaded')
                          )}
                        </div>
                      </div>
                      <label className={styles.fileButton}>
                        {t('helpArticles.uploadImage')}
                        <input
                          type="file"
                          accept={heroImageAccept}
                          onChange={(e) => void handleHeroImageChange(e)}
                          disabled={uploadHeroMutation.isPending}
                        />
                      </label>
                    </div>
                  ) : null}

                  {mediaTab === 'video' ? (
                    <div className={styles.uploadRow}>
                      <div>
                        <div className={styles.uploadTitle}>{t('helpArticles.uploadVideo')}</div>
                        <div className={styles.uploadMeta}>
                          {isCreating && pendingVideoFile ? (
                            <div className={styles.pendingHero}>
                              <div className={styles.pendingHeroName} title={pendingVideoFile.name}>
                                {pendingVideoFile.name}
                              </div>
                              <p className={styles.pendingHeroHint}>{t('helpArticles.videoPendingHint')}</p>
                              <button
                                type="button"
                                className={styles.clearPendingHero}
                                onClick={() => setPendingVideoFile(null)}
                              >
                                {t('helpArticles.clearPendingVideo')}
                              </button>
                            </div>
                          ) : articleDetail?.hero_video_url ? (
                            t('helpArticles.videoUploaded')
                          ) : (
                            t('helpArticles.noVideoUploaded')
                          )}
                        </div>
                      </div>
                      <label className={styles.fileButton}>
                        {t('helpArticles.uploadVideo')}
                        <input
                          type="file"
                          accept="video/mp4,video/webm,video/quicktime"
                          onChange={(e) => void handleHeroVideoChange(e)}
                          disabled={uploadHeroVideoMutation.isPending}
                        />
                      </label>
                    </div>
                  ) : null}

                  {mediaTab === 'youtube' ? (
                    <div className={styles.youtubeForm}>
                      <Input
                        label={t('helpArticles.youtubeUrlLabel')}
                        placeholder={t('helpArticles.youtubeUrlPlaceholder')}
                        value={youtubeInput}
                        onChange={(event) => {
                          setYoutubeInput(event.target.value);
                          if (youtubeError) setYoutubeError(null);
                        }}
                        disabled={isCreating || setYoutubeMutation.isPending}
                      />
                      {youtubeError ? <p className={styles.errorText}>{youtubeError}</p> : null}
                      <div className={styles.youtubeFormActions}>
                        <Button
                          variant="primary"
                          size="sm"
                          type="button"
                          onClick={() => void handleSaveYoutube()}
                          disabled={
                            isCreating ||
                            setYoutubeMutation.isPending ||
                            youtubeInput.trim().length === 0
                          }
                        >
                          {t('helpArticles.saveYoutube')}
                        </Button>
                      </div>
                      {articleDetail?.hero_kind === 'video_youtube' && articleDetail?.hero_image_url ? (
                        <div className={styles.youtubePreview}>
                          <AuthenticatedImage
                            path={articleDetail.hero_image_url}
                            revision={articleDetail.updated_at}
                            alt=""
                          />
                          <p className={styles.youtubePreviewCaption}>
                            {t('helpArticles.youtubePosterAuto')}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {mediaTab === 'pdf' ? (
                    <>
                    <div className={styles.uploadRow}>
                      <div className={styles.uploadInfo}>
                        <div className={styles.uploadTitle}>{t('helpArticles.heroPdf')}</div>
                        <div className={styles.uploadMeta}>
                          {isCreating && pendingPdfFile ? (
                            <div className={styles.pendingHero}>
                              <div className={styles.pendingHeroName} title={pendingPdfFile.name}>
                                {pendingPdfFile.name}
                              </div>
                              <p className={styles.pendingHeroHint}>{t('helpArticles.pdfPendingHint')}</p>
                              <button
                                type="button"
                                className={styles.clearPendingHero}
                                onClick={() => setPendingPdfFile(null)}
                              >
                                {t('helpArticles.clearPendingPdf')}
                              </button>
                            </div>
                          ) : articleDetail?.hero_pdf_url && articleDetail?.hero_kind === 'pdf' ? (
                            t('helpArticles.pdfUploaded')
                          ) : (
                            t('helpArticles.noPdfUploaded')
                          )}
                        </div>
                        <div id="hero-pdf-requirements" className={styles.uploadMeta}>
                          {t('helpArticles.pdfRequirements', { maxMb: heroPdfMaxMb })}
                        </div>
                        {pdfUploadErrorMessage ? (
                          <p id="hero-pdf-upload-error" className={styles.uploadError} role="alert">
                            {pdfUploadErrorMessage}
                          </p>
                        ) : null}
                      </div>
                      <label className={styles.fileButton}>
                        {uploadHeroPdfMutation.isPending
                          ? t('helpArticles.uploadingPdf')
                          : t('helpArticles.uploadPdf')}
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          aria-describedby={
                            pdfUploadErrorMessage
                              ? 'hero-pdf-requirements hero-pdf-upload-error'
                              : 'hero-pdf-requirements'
                          }
                          aria-invalid={pdfUploadErrorMessage ? true : undefined}
                          onChange={(e) => void handleHeroPdfChange(e)}
                          disabled={uploadHeroPdfMutation.isPending}
                        />
                      </label>
                    </div>
                    {!isCreating && articleDetail?.hero_kind === 'pdf' && articleDetail?.hero_pdf_url ? (
                      <div className={styles.pdfPreview}>
                        <div className={styles.uploadTitle}>{t('helpArticles.pdfPreviewLabel')}</div>
                        <AuthenticatedPdf
                          path={articleDetail.hero_pdf_url}
                          title={articleDetail.title}
                          errorLabel={t('helpArticles.pdfPreviewError')}
                        />
                      </div>
                    ) : null}
                    </>
                  ) : null}

                  <section className={styles.mobilePreviewSection} aria-labelledby="mobile-card-preview-heading">
                    <div className={styles.mobilePreviewHeader}>
                      <div>
                        <h4 id="mobile-card-preview-heading">{t('helpArticles.mobilePreviewTitle')}</h4>
                        <p>{t('helpArticles.mobilePreviewDescription')}</p>
                      </div>
                      {hasDedicatedPreview ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => void handleRemovePreviewImage()}
                          disabled={deletePreviewMutation.isPending}
                        >
                          {t('helpArticles.removePreviewImage')}
                        </Button>
                      ) : null}
                    </div>

                    <div className={styles.mobilePreviewFrame}>
                      <AuthenticatedImage
                        path={previewPath}
                        file={previewFile}
                        revision={articleDetail?.updated_at}
                        alt={t('helpArticles.mobilePreviewAlt')}
                        className={styles.mobilePreviewImage}
                        style={{
                          objectFit: editor.previewFit,
                          objectPosition: `${editor.previewPositionX}% ${editor.previewPositionY}%`,
                        }}
                        fallback={
                          <span className={styles.mobilePreviewEmpty}>
                            {t('helpArticles.mobilePreviewEmpty')}
                          </span>
                        }
                        errorFallback={
                          <span className={styles.mobilePreviewEmpty}>
                            {t('helpArticles.mobilePreviewLoadError')}
                          </span>
                        }
                      />
                    </div>

                    <p className={styles.previewSourceHint}>
                      {hasDedicatedPreview
                        ? t('helpArticles.previewDedicatedHint')
                        : hasHeroFallback
                          ? t('helpArticles.previewHeroFallbackHint')
                          : t('helpArticles.previewNoFallbackHint')}
                    </p>

                    <div className={styles.previewControls}>
                      <div className={styles.previewUploadControl}>
                        <label className={styles.fileButton}>
                          {hasDedicatedPreview
                            ? t('helpArticles.replacePreviewImage')
                            : t('helpArticles.uploadPreviewImage')}
                          <input
                            type="file"
                            accept={previewImageAccept}
                            aria-describedby={
                              previewUploadErrorMessage
                                ? 'preview-image-requirements preview-image-upload-error'
                                : 'preview-image-requirements'
                            }
                            aria-invalid={previewUploadErrorMessage ? true : undefined}
                            onChange={(event) => void handlePreviewImageChange(event)}
                            disabled={uploadPreviewMutation.isPending}
                          />
                        </label>
                        <span id="preview-image-requirements">
                          {t('helpArticles.previewImageFormats')}
                        </span>
                        {previewUploadErrorMessage ? (
                          <p
                            id="preview-image-upload-error"
                            className={styles.uploadError}
                            role="alert"
                          >
                            {previewUploadErrorMessage}
                          </p>
                        ) : null}
                      </div>

                      <Select
                        id="mobile-preview-fit"
                        label={t('helpArticles.previewFitLabel')}
                        value={editor.previewFit}
                        onChange={(event) =>
                          setEditor((current) => ({
                            ...current,
                            previewFit: event.target.value as HelpArticlePreviewFit,
                          }))
                        }
                      >
                        <option value="cover">{t('helpArticles.previewFitCover')}</option>
                        <option value="contain">{t('helpArticles.previewFitContain')}</option>
                      </Select>
                    </div>

                    <div className={styles.positionControls}>
                      <label className={styles.rangeField} htmlFor="mobile-preview-position-x">
                        <span>
                          {t('helpArticles.previewPositionX')}: {editor.previewPositionX}%
                        </span>
                        <input
                          id="mobile-preview-position-x"
                          type="range"
                          min="0"
                          max="100"
                          value={editor.previewPositionX}
                          onChange={(event) =>
                            setEditor((current) => ({
                              ...current,
                              previewPositionX: Number(event.target.value),
                            }))
                          }
                        />
                      </label>
                      <label className={styles.rangeField} htmlFor="mobile-preview-position-y">
                        <span>
                          {t('helpArticles.previewPositionY')}: {editor.previewPositionY}%
                        </span>
                        <input
                          id="mobile-preview-position-y"
                          type="range"
                          min="0"
                          max="100"
                          value={editor.previewPositionY}
                          onChange={(event) =>
                            setEditor((current) => ({
                              ...current,
                              previewPositionY: Number(event.target.value),
                            }))
                          }
                        />
                      </label>
                    </div>
                  </section>

                  {mediaTab !== 'pdf' ? (
                    <>
                  <div className={styles.sectionsHeader}>
                    <h4>{t('helpArticles.sections')}</h4>
                    <Button variant="secondary" size="sm" type="button" onClick={addSection}>
                      {t('helpArticles.addSection')}
                    </Button>
                  </div>

                  <div className={styles.sections}>
                    {editor.sections.map((section, index) => (
                      <Card
                        key={`${selectedArticleId ?? 'new'}-section-${index}`}
                        padding="sm"
                        className={styles.sectionCard}
                      >
                        <Input
                          label={t('helpArticles.sectionHeading')}
                          value={section.heading}
                          onChange={(event) => updateSection(index, { heading: event.target.value })}
                        />
                        <Textarea
                          label={t('helpArticles.sectionBody')}
                          value={section.body}
                          rows={4}
                          onChange={(event) => updateSection(index, { body: event.target.value })}
                        />
                        {editor.sections.length > 1 ? (
                          <div className={styles.sectionCardFooter}>
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              onClick={() => removeSection(index)}
                            >
                              {t('helpArticles.removeSection')}
                            </Button>
                          </div>
                        ) : null}
                      </Card>
                    ))}
                  </div>
                    </>
                  ) : null}

                  <div className={styles.actions}>
                    <Button
                      variant="primary"
                      onClick={() => void handleSave()}
                      disabled={
                        createMutation.isPending ||
                        updateMutation.isPending ||
                        uploadHeroMutation.isPending ||
                        uploadHeroVideoMutation.isPending ||
                        uploadHeroPdfMutation.isPending ||
                        uploadPreviewMutation.isPending ||
                        deletePreviewMutation.isPending
                      }
                    >
                      {selectedArticleId && !isCreating
                        ? t('helpArticles.saveChanges')
                        : t('helpArticles.createDraft')}
                    </Button>
                    {saveErrorMessage ? (
                      <p className={styles.errorText} role="alert">
                        {saveErrorMessage}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
            </Card>
          </div>
          <MobileBlogPreview
            open={mobilePreviewOpen}
            onClose={() => setMobilePreviewOpen(false)}
            title={t('helpArticles.mobileAppPreviewTitle')}
            subtitle={
              mobilePreviewTarget.kind === 'editor'
                ? t('helpArticles.mobileAppPreviewDraftHint')
                : t('helpArticles.mobileAppPreviewListHint')
            }
            closeLabel={t('helpArticles.closeMobileAppPreview')}
            articles={mobileListArticles}
            screen={mobilePreviewTarget.kind === 'list' ? 'list' : 'article'}
            article={mobilePreviewArticle}
            articleLoading={mobilePreviewTarget.kind === 'article' && isPreviewArticleLoading}
            pendingHeroFile={mobilePreviewTarget.kind === 'editor' ? pendingHeroFile : null}
            onOpenArticle={(articleId) => setMobilePreviewTarget({ kind: 'article', articleId })}
            onShowList={() => setMobilePreviewTarget({ kind: 'list' })}
          />
        </div>
      </AppShell>
    </PermissionGate>
  );
}
